import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  BookOpen, 
  ShoppingCart, 
  Headphones, 
  Smartphone,
  ExternalLink,
  Star,
  CheckCircle,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useBook, useBooks } from '../hooks/useContent';
import { RichTextRenderer } from '../components/RichTextRenderer';

export default function BookDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  // Fetch the specific book from Contentful (with static fallback)
  const { book, isLoading, error } = useBook(slug);
  // Fetch all books for related books
  const { books: allBooks } = useBooks();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-secondary" />
        <span className="ml-2 text-slate-600">Loading book...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold text-brand-primary mb-4">
            Error Loading Book
          </h1>
          <p className="text-slate-600 mb-8">{error}</p>
          <Button
            asChild
            className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90"
          >
            <Link to="/books">Back to Books</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="book-not-found">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold text-brand-primary mb-4">
            Book Not Found
          </h1>
          <p className="text-slate-600 mb-8">
            The book you&apos;re looking for doesn&apos;t exist or has been moved.
          </p>
          <Button
            asChild
            className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90"
          >
            <Link to="/books">Back to Books</Link>
          </Button>
        </div>
      </div>
    );
  }

  const otherBooks = allBooks.filter((b) => b.id !== book.id);

  return (
    <div data-testid="book-detail-page">
      {/* Hero Section */}
      <section className="py-12 md:py-20 bg-brand-accent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center text-slate-600 hover:text-brand-primary mb-8 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Books
            </button>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-start">
            {/* Book Cover */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="flex justify-center lg:sticky lg:top-32"
            >
              <div className="relative">
                {/* 3D Book Effect */}
                <div className="absolute -right-4 top-4 bottom-4 w-6 bg-gradient-to-r from-slate-300 to-slate-200 rounded-r-lg shadow-inner" />
                <div className="absolute -bottom-4 left-4 right-0 h-6 bg-gradient-to-b from-slate-300 to-slate-200 rounded-b-lg shadow-inner" />
                <img
                  src={book.coverImage}
                  alt={book.title}
                  className="relative w-72 md:w-80 lg:w-96 rounded-lg shadow-2xl"
                />
              </div>
            </motion.div>

            {/* Book Details */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-brand-primary mb-2">
                {book.title}
              </h1>
              <p className="text-brand-secondary text-xl md:text-2xl font-medium mb-6">
                {book.subtitle}
              </p>

              {/* Book Meta */}
              <div className="flex flex-wrap gap-4 mb-6 text-sm text-slate-600">
                <span className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-brand-secondary" />
                  {book.pages} pages
                </span>
                <span>Published: {book.publishDate}</span>
                <span>{book.publisher}</span>
              </div>

              {/* Short Description */}
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                {book.description}
              </p>

              {/* Purchase Buttons */}
              <div className="flex flex-wrap gap-4 mb-6">
                {book.amazonUrl && (
                  <Button
                    asChild
                    size="lg"
                    className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90 font-semibold shadow-lg shadow-orange-500/20"
                  >
                    <a href={book.amazonUrl} target="_blank" rel="noopener noreferrer">
                      <ShoppingCart className="w-5 h-5 mr-2" />
                      Buy on Amazon
                    </a>
                  </Button>
                )}
                {book.audibleUrl && (
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="rounded-full border-2 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white font-semibold"
                  >
                    <a href={book.audibleUrl} target="_blank" rel="noopener noreferrer">
                      <Headphones className="w-5 h-5 mr-2" />
                      Audiobook
                    </a>
                  </Button>
                )}
              </div>

              {/* Other Purchase Options */}
              {(book.kindleUrl || book.barnesNobleUrl) && (
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="text-slate-500">Also available:</span>
                  {book.kindleUrl && (
                    <a
                      href={book.kindleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-brand-secondary hover:underline"
                    >
                      <Smartphone className="w-4 h-4" />
                      Kindle
                    </a>
                  )}
                  {book.barnesNobleUrl && (
                    <a
                      href={book.barnesNobleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-brand-secondary hover:underline"
                    >
                      Barnes & Noble
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Full Description */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-primary mb-8">
              About This Book
            </h2>
            <div className="prose prose-lg max-w-none">
              {/* Handle Rich Text from Contentful */}
              {book.fullDescriptionRaw && typeof book.fullDescriptionRaw === 'object' ? (
                <RichTextRenderer content={book.fullDescriptionRaw} />
              ) : typeof book.fullDescription === 'string' && book.fullDescription ? (
                /* Handle string from static data */
                book.fullDescription.split('\n\n').map((paragraph, index) => {
                  if (paragraph.startsWith('•')) {
                    const items = paragraph.split('\n').filter(item => item.startsWith('•'));
                    return (
                      <ul key={index} className="space-y-3 my-6">
                        {items.map((item, i) => (
                          <li key={i} className="flex items-start gap-3 text-slate-600">
                            <CheckCircle className="w-5 h-5 text-brand-secondary flex-shrink-0 mt-0.5" />
                            <span>{item.replace('• ', '')}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return (
                    <p key={index} className="text-slate-600 leading-relaxed mb-6">
                      {paragraph}
                    </p>
                  );
                })
              ) : book.description ? (
                /* Fallback to short description */
                <p className="text-slate-600 leading-relaxed">{book.description}</p>
              ) : null}
            </div>
          </motion.div>

          {/* Key Takeaways */}
          {book.keyTakeaways && book.keyTakeaways.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 bg-brand-accent rounded-2xl p-8"
            >
              <h3 className="font-heading text-xl font-bold text-brand-primary mb-6">
                Key Takeaways
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {book.keyTakeaways.map((takeaway, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-brand-secondary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-brand-secondary font-bold text-sm">{index + 1}</span>
                    </div>
                    <p className="text-slate-600">{takeaway}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Chapters */}
      {book.chapters && book.chapters.length > 0 && (
        <section className="py-16 md:py-24 bg-brand-accent">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-primary mb-8">
                What's Inside
              </h2>
              <div className="space-y-4">
                {book.chapters.map((chapter, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-4 bg-white p-4 rounded-xl"
                  >
                    <span className="w-10 h-10 rounded-full bg-brand-secondary text-white flex items-center justify-center font-bold">
                      {index + 1}
                    </span>
                    <span className="text-brand-primary font-medium">{chapter}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {book.testimonials && book.testimonials.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-primary">
                What Readers Say
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {book.testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-brand-accent rounded-2xl p-6 md:p-8"
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-brand-secondary text-brand-secondary" />
                    ))}
                  </div>
                  <blockquote className="text-brand-primary font-medium text-lg mb-4">
                    "{testimonial.quote}"
                  </blockquote>
                  <cite className="not-italic">
                    <p className="font-semibold text-brand-primary">{testimonial.author}</p>
                    <p className="text-sm text-slate-600">{testimonial.title}</p>
                  </cite>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Other Books */}
      {otherBooks.length > 0 && (
        <section className="py-16 md:py-24 bg-brand-accent">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-primary">
                More Books
              </h2>
              <Link
                to="/books"
                className="text-brand-secondary font-medium flex items-center hover:gap-2 transition-all"
              >
                View All <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {otherBooks.map((otherBook, index) => (
                <motion.div
                  key={otherBook.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/books/${otherBook.slug}`}
                    className="flex gap-6 bg-white rounded-2xl p-6 hover:shadow-xl transition-all duration-300 group"
                  >
                    <img
                      src={otherBook.coverImage}
                      alt={otherBook.title}
                      className="w-24 md:w-32 rounded-lg shadow-md group-hover:scale-105 transition-transform"
                    />
                    <div>
                      <h3 className="font-heading text-xl font-bold text-brand-primary group-hover:text-brand-secondary transition-colors">
                        {otherBook.title}
                      </h3>
                      <p className="text-brand-secondary text-sm font-medium mb-2">
                        {otherBook.subtitle}
                      </p>
                      <p className="text-slate-600 text-sm line-clamp-2">
                        {otherBook.description}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
